import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Loader2, RefreshCw } from 'lucide-react';
import { isInvoicePreviewFrozen } from '@repo/api-client';
import { useActiveOrganizationId } from '@repo/api-client/react';
import type { Invoice } from '@repo/api-client/dtos';
import {
  useInvoice,
  useInvoicePreview,
  useOrganization,
  useClient,
  useRenderInvoice,
} from '@repo/queries';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert } from '@/components/ui/alert';
import { describeAppError } from '@/features/common/describeAppError';
import { TemplateCanvas } from '@/features/preview/TemplateCanvas';
import { FrozenArtifactPreview } from '@/features/preview/FrozenArtifactPreview';
import { buildArtboardPayload } from '@/features/preview/buildArtboardPayload';
import { CustomizeToolbar } from '@/features/preview/CustomizeToolbar';
import { useCustomize } from '@/features/preview/useCustomize';

function resolveFrozenPreviewUrl(
  invoice: Invoice,
  preview: { status: string; previewUrl?: string | null } | null,
): string | null {
  if (invoice.finalDocument?.status === 'ready' && invoice.finalDocument.previewUrl) {
    return invoice.finalDocument.previewUrl;
  }
  if (preview?.status === 'ready' && preview.previewUrl) {
    return preview.previewUrl;
  }
  return null;
}

function resolveFrozenPdfUrl(
  invoice: Invoice,
  preview: { status: string; pdfUrl?: string | null } | null,
): string | null {
  if (invoice.finalDocument?.status === 'ready' && invoice.finalDocument.pdfUrl) {
    return invoice.finalDocument.pdfUrl;
  }
  if (preview?.status === 'ready' && preview.pdfUrl) {
    return preview.pdfUrl;
  }
  return null;
}

function usePreviewHostWidth(): { hostRef: React.RefObject<HTMLDivElement | null>; hostWidth: number } {
  const hostRef = useRef<HTMLDivElement>(null);
  const [hostWidth, setHostWidth] = useState<number>(794);
  useLayoutEffect(() => {
    const node = hostRef.current;
    if (!node) return;
    const update = () => {
      const w = node.clientWidth;
      if (w > 0) setHostWidth(w);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);
  return { hostRef, hostWidth };
}

function DraftInvoicePreviewBody({
  invoice,
  organization,
  client,
  hostRef,
  hostWidth,
}: {
  invoice: Invoice;
  organization: NonNullable<ReturnType<typeof useOrganization>['organization']>;
  client: ReturnType<typeof useClient>['client'];
  hostRef: React.RefObject<HTMLDivElement | null>;
  hostWidth: number;
}) {
  const customize = useCustomize({ organization, invoice });

  const payload =
    customize.effectiveOrg && invoice
      ? buildArtboardPayload({
          invoice,
          organization: customize.effectiveOrg as typeof organization,
          client: client ?? null,
          templateId: customize.effectiveTemplateId,
        })
      : null;

  return (
    <>
      <div
        ref={hostRef}
        className="rounded-xl border border-line bg-muted/40 p-4 pb-20 shadow-sm sm:p-6 sm:pb-24"
      >
        {payload ? (
          <div className="mx-auto" style={{ maxWidth: 794 }}>
            <TemplateCanvas
              payload={payload}
              templateId={customize.effectiveTemplateId}
              width={Math.min(hostWidth - 32, 794)}
              className="rounded-md shadow-md ring-1 ring-line"
            />
          </div>
        ) : (
          <div className="flex h-[60vh] items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 size-4 animate-spin" /> Preparing preview…
          </div>
        )}
      </div>

      <CustomizeToolbar
        templateId={customize.effectiveTemplateId}
        onPickTemplate={customize.setTemplate}
        accent={customize.effectiveAccent}
        onPickAccent={customize.setAccent}
        showLogo={customize.effectiveShowLogo}
        onToggleShowLogo={customize.setShowLogo}
        showFooter={customize.effectiveShowFooter}
        onToggleShowFooter={customize.setShowFooter}
        showItemDiscount={customize.effectiveShowItemDiscount}
        onToggleShowItemDiscount={customize.setShowItemDiscount}
        showItemTax={customize.effectiveShowItemTax}
        onToggleShowItemTax={customize.setShowItemTax}
        showItemNotes={customize.effectiveShowItemNotes}
        onToggleShowItemNotes={customize.setShowItemNotes}
        saving={customize.saving}
        className="fixed inset-x-0 bottom-6"
      />
    </>
  );
}

function FrozenInvoicePreviewBody({
  invoice,
  organization,
  client,
  hostRef,
  hostWidth,
  orgId,
  invoiceId,
}: {
  invoice: Invoice;
  organization: NonNullable<ReturnType<typeof useOrganization>['organization']>;
  client: ReturnType<typeof useClient>['client'];
  hostRef: React.RefObject<HTMLDivElement | null>;
  hostWidth: number;
  orgId: string;
  invoiceId: string;
}) {
  const hasFinalPreviewUrl =
    invoice.finalDocument?.status === 'ready' && !!invoice.finalDocument.previewUrl;

  const { preview, refetch } = useInvoicePreview(orgId, invoiceId, {
    enabled: !hasFinalPreviewUrl,
  });
  const { renderAndWait, loading: resolving, error: renderError } = useRenderInvoice(
    orgId,
    invoiceId,
  );

  const previewUrl = resolveFrozenPreviewUrl(invoice, preview);
  const needsPrinterFetch =
    !previewUrl && invoice.finalDocument?.status !== 'failed';

  const resolveStartedRef = useRef(false);
  const [waitingForArtifact, setWaitingForArtifact] = useState(
    needsPrinterFetch && !hasFinalPreviewUrl,
  );

  useEffect(() => {
    if (previewUrl) {
      setWaitingForArtifact(false);
      resolveStartedRef.current = false;
      return;
    }
    if (!needsPrinterFetch || !orgId || !invoiceId) {
      setWaitingForArtifact(false);
      return;
    }
    if (resolveStartedRef.current) return;
    resolveStartedRef.current = true;
    setWaitingForArtifact(true);

    void renderAndWait({}, { timeoutMs: 60_000 })
      .catch(() => {
        resolveStartedRef.current = false;
      })
      .finally(() => {
        setWaitingForArtifact(false);
      });
  }, [previewUrl, needsPrinterFetch, orgId, invoiceId, renderAndWait]);

  const onRetry = () => {
    resolveStartedRef.current = false;
    setWaitingForArtifact(true);
    void renderAndWait({}, { timeoutMs: 60_000 })
      .then(() => refetch())
      .catch(() => {
        resolveStartedRef.current = false;
      })
      .finally(() => {
        setWaitingForArtifact(false);
      });
  };

  return (
    <>
      {renderError ? (
        <Alert variant="danger" className="mb-4">
          {describeAppError(renderError).message}
        </Alert>
      ) : null}

      <div
        ref={hostRef}
        className="rounded-xl border border-line bg-muted/40 p-4 shadow-sm sm:p-6"
      >
        <div className="mx-auto flex justify-center" style={{ maxWidth: 794 }}>
          <FrozenArtifactPreview
            invoice={invoice}
            organization={organization}
            client={client ?? null}
            width={Math.min(hostWidth - 32, 794)}
            className="rounded-md shadow-md ring-1 ring-line"
            previewUrl={previewUrl}
            documentStatus={invoice.finalDocument?.status ?? null}
            documentError={invoice.finalDocument?.errorMessage ?? null}
            waitingForArtifact={waitingForArtifact || resolving}
            onRetry={onRetry}
          />
        </div>
      </div>
    </>
  );
}

function DraftInvoicePreviewActions({
  orgId,
  invoiceId,
}: {
  orgId: string;
  invoiceId: string;
}) {
  const { preview, refetch } = useInvoicePreview(orgId, invoiceId);
  const { render, loading: rendering } = useRenderInvoice(orgId, invoiceId);

  useEffect(() => {
    if (!preview && orgId && invoiceId && !rendering) {
      void render({}).then(() => refetch());
    }
  }, [preview, orgId, invoiceId, rendering, render, refetch]);

  const pdfReady = preview?.status === 'ready' && !!preview.pdfUrl;

  return (
    <>
      <Button
        variant="outline"
        onClick={() => {
          void render({}).then(() => refetch());
        }}
        disabled={rendering}
      >
        {rendering ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <RefreshCw className="size-4" />
        )}
        Refresh PDF
      </Button>
      {pdfReady ? (
        <Button asChild>
          <a href={preview!.pdfUrl!} download>
            <Download className="size-4" /> Download PDF
          </a>
        </Button>
      ) : (
        <Button disabled>
          <Loader2 className="size-4 animate-spin" /> Preparing PDF…
        </Button>
      )}
    </>
  );
}

function FrozenInvoicePreviewActions({
  invoice,
  orgId,
  invoiceId,
}: {
  invoice: Invoice;
  orgId: string;
  invoiceId: string;
}) {
  const hasFinalPdfUrl =
    invoice.finalDocument?.status === 'ready' && !!invoice.finalDocument.pdfUrl;
  const { preview } = useInvoicePreview(orgId, invoiceId, { enabled: !hasFinalPdfUrl });
  const pdfUrl = resolveFrozenPdfUrl(invoice, preview);

  if (!pdfUrl) {
    return (
      <Button disabled>
        <Loader2 className="size-4 animate-spin" /> Preparing PDF…
      </Button>
    );
  }

  return (
    <Button asChild>
      <a href={pdfUrl} download>
        <Download className="size-4" /> Download PDF
      </a>
    </Button>
  );
}

export function InvoicePreviewScreen() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const orgId = useActiveOrganizationId();
  const { invoice, loading: invoiceLoading } = useInvoice(invoiceId ?? '');
  const { organization } = useOrganization(orgId ?? undefined);
  const { client } = useClient(invoice?.clientId ?? '', { enabled: !!invoice?.clientId });
  const { hostRef, hostWidth } = usePreviewHostWidth();

  if (invoiceLoading) {
    return (
      <Page>
        <Skeleton className="h-10 w-72" />
        <Skeleton className="mt-6 h-[80vh] rounded-xl" />
      </Page>
    );
  }

  if (!invoice) {
    return (
      <Page>
        <Alert variant="danger">Invoice not found.</Alert>
      </Page>
    );
  }

  if (!organization || !orgId || !invoiceId) {
    return (
      <Page>
        <Alert variant="danger">Workspace not ready.</Alert>
      </Page>
    );
  }

  const frozen = isInvoicePreviewFrozen(invoice.status);

  return (
    <Page>
      <Button variant="link" size="sm" asChild className="-ml-2 mb-4">
        <Link to={`/invoices/${invoice.id}`}>
          <ArrowLeft className="size-4" /> Back to {invoice.invoiceNumber}
        </Link>
      </Button>

      <PageHeader
        title={`Preview · ${invoice.invoiceNumber}`}
        description={
          frozen
            ? 'Issued copy — layout is locked to what was sent.'
            : 'Live render — exactly the document your recipient will see.'
        }
        actions={
          frozen ? (
            <FrozenInvoicePreviewActions invoice={invoice} orgId={orgId} invoiceId={invoiceId} />
          ) : (
            <DraftInvoicePreviewActions orgId={orgId} invoiceId={invoiceId} />
          )
        }
      />

      {frozen ? (
        <FrozenInvoicePreviewBody
          invoice={invoice}
          organization={organization}
          client={client}
          hostRef={hostRef}
          hostWidth={hostWidth}
          orgId={orgId}
          invoiceId={invoiceId}
        />
      ) : (
        <DraftInvoicePreviewBody
          invoice={invoice}
          organization={organization}
          client={client}
          hostRef={hostRef}
          hostWidth={hostWidth}
        />
      )}
    </Page>
  );
}
